// Envuelve `navigator.geolocation.watchPosition`. Devuelve un handle con:
//   - start(cb?): abre el watch si no está abierto. cb (opcional) se dispara con el primer fix.
//   - stop(): cierra el watch y suelta el wake lock.
//   - onFix(cb): suscripción continua a nuevas posiciones.
//   - setFollow(bool): dice al mapa si debe centrar. NO paramos el GPS al dejar de seguir.
//   - lastPos(): última posición conocida.
//
// El botón GPS y el arrastre del mapa manipulan `setFollow`; el resto del flujo (dibujar el
// punto azul, círculo de precisión) queda dentro de este módulo.

import type { LatLng } from "../geo";
import type { MapAdapter } from "../map/types";
import { keepAwake } from "./wakeLock";

export interface GpsHandle {
  start(onFirstFix?: (ll: LatLng) => void): boolean;
  stop(): void;
  onFix(cb: (ll: LatLng) => void): () => void;
  setFollow(follow: boolean): void;
  isFollowing(): boolean;
  lastPos(): LatLng | null;
}

export interface GpsOptions {
  adapter: MapAdapter;
  toast: (msg: string) => void;
  onFollowChange?: (following: boolean) => void;
}

export function createGps(o: GpsOptions): GpsHandle {
  let watchId: number | null = null;
  let last: LatLng | null = null;
  let following = false;
  const fixCbs: ((ll: LatLng) => void)[] = [];
  let firstFixCb: ((ll: LatLng) => void) | null = null;

  const changeFollow = (next: boolean): void => {
    if (following === next) return;
    following = next;
    o.adapter.setFollowUser(next);
    keepAwake(next);
    o.onFollowChange?.(next);
  };

  const start = (onFirstFix?: (ll: LatLng) => void): boolean => {
    if (!("geolocation" in navigator) || !window.isSecureContext) {
      o.toast("La ubicación solo funciona si la página se sirve por HTTPS.");
      return false;
    }
    if (onFirstFix) firstFixCb = onFirstFix;
    if (watchId !== null) {
      if (firstFixCb && last) {
        firstFixCb(last);
        firstFixCb = null;
      }
      return true;
    }
    watchId = navigator.geolocation.watchPosition(
      (p) => {
        const ll: LatLng = { lat: p.coords.latitude, lon: p.coords.longitude };
        last = ll;
        o.adapter.setUserPosition(ll, p.coords.accuracy);
        for (const cb of fixCbs) cb(ll);
        if (firstFixCb) {
          firstFixCb(ll);
          firstFixCb = null;
        }
      },
      (err) => {
        o.toast(err.code === err.PERMISSION_DENIED ? "Permiso de ubicación denegado." : "No se pudo obtener la ubicación.");
        stop();
      },
      { enableHighAccuracy: true, maximumAge: 5000, timeout: 15000 },
    );
    return true;
  };

  const stop = (): void => {
    if (watchId !== null) {
      navigator.geolocation.clearWatch(watchId);
      watchId = null;
    }
    changeFollow(false);
    o.adapter.setUserPosition(null, 0);
  };

  // El adaptador nos avisa cuando el usuario arrastra el mapa: paramos el follow pero mantenemos
  // la lectura del GPS activa (el prototipo hace lo mismo).
  o.adapter.onUserDrag(() => changeFollow(false));

  return {
    start,
    stop,
    onFix(cb) {
      fixCbs.push(cb);
      return () => {
        const i = fixCbs.indexOf(cb);
        if (i >= 0) fixCbs.splice(i, 1);
      };
    },
    setFollow: changeFollow,
    isFollowing: () => following,
    lastPos: () => last,
  };
}
